import React, { useState, useEffect, useCallback } from "react";
import { Form } from "react-bootstrap";
import { GitGraph, RefreshCw, Filter } from "lucide-react";
import * as plmSvc from "../../services/plmService";

const TraceabilityTab = ({ token, plm, selectedProductId }) => {
  const [view, setView] = useState("report");
  const [report, setReport] = useState([]);
  const [coverage, setCoverage] = useState([]);
  const [loading, setLoading] = useState(false);

  const [versions, setVersions] = useState([]);
  const [selectedVersionId, setSelectedVersionId] = useState("");

  useEffect(() => {
    if (!selectedProductId) { setVersions([]); setSelectedVersionId(""); return; }
    plmSvc.fetchVersionsApi({ token, productId: selectedProductId })
      .then(setVersions)
      .catch(() => setVersions([]));
  }, [selectedProductId, token]);

  const loadData = useCallback(async () => {
    setLoading(true);
    try {
      if (view === "report") {
        const data = await plmSvc.fetchTraceabilityReportApi({ token, productId: selectedProductId || undefined, productVersionId: selectedVersionId || undefined });
        setReport(data);
      } else {
        const data = await plmSvc.fetchCoverageSummaryApi({ token, productId: selectedProductId || undefined, productVersionId: selectedVersionId || undefined });
        setCoverage(data);
      }
    } catch { view === "report" ? setReport([]) : setCoverage([]); }
    finally { setLoading(false); }
  }, [token, view, selectedProductId, selectedVersionId]);

  useEffect(() => { loadData(); }, [loadData]);

  const execBadgeColor = (s) => {
    switch (s) { case "Passed": return "green"; case "Failed": return "red"; default: return "yellow"; }
  };

  const progressClass = (rate) => {
    if (rate === 100) return "green";
    if (rate > 50) return "yellow";
    return "red";
  };

  return (
    <div>
      <div className="plm-section-header">
        <h2 className="plm-section-title">
          <span className="icon-wrap"><GitGraph size={20} /></span>
          Traceability
        </h2>
        <div className="d-flex gap-2 align-items-center">
          <div className="plm-toggle-group">
            <button className={`plm-toggle-btn ${view === "report" ? "active" : ""}`}
              onClick={() => setView("report")}>Report</button>
            <button className={`plm-toggle-btn ${view === "coverage" ? "active" : ""}`}
              onClick={() => setView("coverage")}>Coverage</button>
          </div>
          <button className="plm-btn-ghost" onClick={loadData}><RefreshCw size={14} /></button>
        </div>
      </div>

      {versions.length > 0 && (
        <div className="plm-filter-bar">
          <Filter size={14} style={{ color: "var(--plm-text-muted)" }} />
          <span style={{ fontSize: "0.82rem", fontWeight: 600 }}>Version</span>
          <Form.Select size="sm" style={{ maxWidth: 240 }} value={selectedVersionId}
            onChange={(e) => setSelectedVersionId(e.target.value)}>
            <option value="">All Versions</option>
            {versions.map((v) => <option key={v.id} value={v.id}>{v.versionLabel}</option>)}
          </Form.Select>
        </div>
      )}

      {loading ? (
        <div className="plm-loading"><div className="plm-loading-dots"><span /><span /><span /></div></div>
      ) : view === "report" ? (
        report.length === 0 ? (
          <div className="plm-empty">
            <div className="plm-empty-icon"><GitGraph size={28} /></div>
            <div className="plm-empty-title">No traceability data</div>
            <div className="plm-empty-desc">Link requirements to journey steps and create test cases to see the full trace.</div>
          </div>
        ) : (
          <div style={{ borderRadius: "var(--plm-radius)", overflow: "hidden", boxShadow: "var(--plm-shadow)" }}>
            <table className="plm-table">
              <thead>
                <tr>
                  <th>Journey</th>
                  <th>Persona</th>
                  <th>Phase</th>
                  <th>Requirement</th>
                  <th>Test Case</th>
                  <th>Status</th>
                  <th>Version</th>
                </tr>
              </thead>
              <tbody>
                {report.map((row, i) => (
                  <tr key={i}>
                    <td style={{ fontWeight: 600 }}>{row.journey_map_title}</td>
                    <td>{row.persona_name || <span style={{ color: "var(--plm-text-muted)" }}>--</span>}</td>
                    <td>
                      <span className="plm-badge purple" style={{ marginRight: 4 }}>#{row.step_order}</span>
                      {row.phase_name}
                    </td>
                    <td>{row.requirement_title}</td>
                    <td>{row.test_case_title || <span style={{ color: "var(--plm-text-muted)" }}>--</span>}</td>
                    <td>
                      {row.execution_status ? (
                        <span className={`plm-badge ${execBadgeColor(row.execution_status)}`}>
                          {row.execution_status}
                        </span>
                      ) : (
                        <span style={{ color: "var(--plm-text-muted)" }}>--</span>
                      )}
                    </td>
                    <td>{row.app_version || <span style={{ color: "var(--plm-text-muted)" }}>--</span>}</td>
                  </tr>
                ))}
              </tbody>
            </table>
          </div>
        )
      ) : (
        coverage.length === 0 ? (
          <div className="plm-empty">
            <div className="plm-empty-icon"><GitGraph size={28} /></div>
            <div className="plm-empty-title">No coverage data</div>
            <div className="plm-empty-desc">Add requirements and test cases to track coverage.</div>
          </div>
        ) : (
          <div className="d-flex flex-column gap-3">
            {coverage.map((r) => {
              const passRate = r.totalExecutions > 0 ? Math.round((r.passed / r.totalExecutions) * 100) : 0;
              return (
                <div key={r.requirementId} className="plm-coverage-card">
                  <div className="d-flex justify-content-between align-items-center mb-3">
                    <div style={{ fontWeight: 700 }}>{r.requirementTitle}</div>
                    <div className="d-flex gap-3" style={{ fontSize: "0.78rem", color: "var(--plm-text-muted)" }}>
                      <span>{r.coveredJourneySteps} step(s)</span>
                      <span>{r.totalTestCases} test(s)</span>
                    </div>
                  </div>
                  <div className="d-flex align-items-center gap-3">
                    <div className="plm-progress-bar" style={{ flex: 1 }}>
                      <div
                        className={`plm-progress-fill ${progressClass(passRate)}`}
                        style={{ width: `${passRate}%` }}
                      />
                    </div>
                    <span style={{ fontSize: "0.78rem", minWidth: 60, textAlign: "right", fontWeight: 600 }}>
                      {r.passed}/{r.totalExecutions}
                    </span>
                  </div>
                  {r.failed > 0 && (
                    <div style={{ color: "var(--plm-danger)", fontSize: "0.78rem", marginTop: 6 }}>
                      {r.failed} failed execution(s)
                    </div>
                  )}
                </div>
              );
            })}
          </div>
        )
      )}
    </div>
  );
};

export default TraceabilityTab;
